import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/Button";
import { MemberBookingsCalendar } from "@/components/MemberBookingsCalendar";
import { ApiError, memberApi } from "@/lib/api";
import { useLogout, useMe, useMemberToken } from "@/hooks/useMemberAuth";
import { useRooms } from "@/hooks/useRooms";
import { formatDate, formatPriceEur, formatRange } from "@/lib/format";
import type { BookingStatus, MemberBookingItem } from "@/types/api";

const STATUS_LABEL: Record<BookingStatus, string> = {
  draft: "Entwurf",
  pending_admin: "Wartet auf Freigabe",
  awaiting_payment: "Bezahlung offen",
  confirmed: "Bestätigt",
  cancelled: "Storniert",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
};

// Bekannte Alt-Slugs mit hübschem Label; neue Angebote fallen auf den Slug zurück.
const BOOKING_TYPE_LABEL: Record<string, string> = {
  training: "Training",
  privatstunde: "Privatstunde",
  workshop: "Workshop",
};

const STATUS_TONE: Record<BookingStatus, string> = {
  draft: "bg-line text-ink-soft",
  pending_admin: "bg-primary/10 text-primary",
  awaiting_payment: "bg-info/10 text-info",
  confirmed: "bg-success/10 text-success",
  cancelled: "bg-ink-muted/10 text-ink-muted",
  rejected: "bg-danger/10 text-danger",
  expired: "bg-ink-muted/10 text-ink-muted",
};

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function BookingRow({
  booking,
  roomName,
}: {
  booking: MemberBookingItem;
  roomName: string;
}) {
  const download = useMutation<Blob, ApiError, number>({
    mutationFn: memberApi.downloadIcs,
    onSuccess: (blob) => triggerDownload(blob, `tanzraum-buchung-${booking.id}.ics`),
  });

  return (
    <div className="rounded-card border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-caption uppercase tracking-wider text-ink-muted">{roomName}</p>
          <p className="font-display text-h4 text-ink">{formatRange(booking.starts_at, booking.ends_at)}</p>
          <p className="mt-1 text-body-sm text-ink-soft">
            {BOOKING_TYPE_LABEL[booking.booking_type] ?? booking.booking_type}{" "}
            · {formatPriceEur(booking.price)}
          </p>
          {booking.purpose && (
            <p className="mt-2 text-body-sm text-ink-muted italic">"{booking.purpose}"</p>
          )}
        </div>
        <span
          className={`rounded-full px-3 py-1 text-caption font-medium uppercase tracking-wider ${
            STATUS_TONE[booking.status]
          }`}
        >
          {STATUS_LABEL[booking.status]}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {booking.status === "awaiting_payment" ? (
          <>
            {booking.pay_url ? (
              <a
                href={booking.pay_url}
                className="inline-flex items-center justify-center rounded-card bg-primary px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Jetzt bezahlen
              </a>
            ) : null}
            {booking.payment_due_at ? (
              <p className="text-body-sm text-ink-soft">
                Bitte bezahlen bis {formatDate(booking.payment_due_at, "EEE, dd.MM.yyyy, HH:mm")} Uhr
              </p>
            ) : null}
          </>
        ) : booking.status === "confirmed" ? (
          <>
            <Button
              size="sm"
              onClick={() => download.mutate(booking.id)}
              loading={download.isPending}
            >
              Kalender-Datei (.ics) herunterladen
            </Button>
            <p className="text-caption text-ink-muted">
              Import in Google Kalender, Apple Kalender oder Outlook.
            </p>
          </>
        ) : (
          <p className="text-caption text-ink-muted">
            Kalender-Datei ist erst nach Bestätigung durch Charlie verfügbar.
          </p>
        )}
        {download.error && (
          <p className="w-full text-caption text-danger">{download.error.message}</p>
        )}
      </div>
    </div>
  );
}

export function MyBookingsPage() {
  const navigate = useNavigate();
  const token = useMemberToken();
  const logout = useLogout();
  const me = useMe();
  const { data: rooms } = useRooms();

  const bookings = useQuery<MemberBookingItem[], ApiError>({
    queryKey: ["member", "bookings"],
    queryFn: memberApi.bookings,
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (!token) navigate("/konto/login", { replace: true });
  }, [token, navigate]);

  if (!token) return null;

  const roomName = (roomId: number) =>
    rooms?.find((r) => r.id === roomId)?.name ?? `Raum #${roomId}`;

  return (
    <div className="mx-auto max-w-container px-6 py-12">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 font-display text-h1 uppercase text-primary">Meine Buchungen</h1>
          {me.data && (
            <p className="text-body-sm text-ink-soft">
              Eingeloggt als <span className="font-medium text-ink">{me.data.full_name}</span> ·{" "}
              {me.data.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/konto/profil"
            className="inline-flex items-center rounded border border-line bg-surface px-3 py-1.5 text-body-sm font-medium text-ink-soft transition-colors hover:border-primary hover:text-primary"
          >
            Profil bearbeiten
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
              navigate("/konto/login");
            }}
          >
            Abmelden
          </Button>
        </div>
      </div>

      {bookings.isPending && (
        <p className="text-body-sm text-ink-muted">Lade Buchungen …</p>
      )}

      {bookings.error && (
        <div className="rounded-card border border-danger bg-danger/5 p-4 text-body-sm text-danger">
          {bookings.error.message}
        </div>
      )}

      {bookings.data && bookings.data.length === 0 && (
        <div className="rounded-card border border-line bg-surface-warm p-8 text-center">
          <p className="mb-4 text-body text-ink-soft">
            Du hast noch keine Buchungen.
          </p>
          <Link
            to="/"
            className="inline-block rounded bg-primary px-5 py-2 text-body-sm font-semibold text-white hover:bg-primary-dark"
          >
            Raum auswählen und buchen
          </Link>
        </div>
      )}

      {bookings.data && bookings.data.length > 0 && (
        <div className="space-y-6">
          {rooms && <MemberBookingsCalendar bookings={bookings.data} rooms={rooms} />}
          <div className="space-y-4">
            {bookings.data.map((b) => (
              <BookingRow key={b.id} booking={b} roomName={roomName(b.room_id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
